/**
 * Ad slots that show nothing in the browser — an ad blocker hid the
 * creative or blocked its image or script — are collapsed, so the page
 * reads as if the slot had never been sold (the PHP views already leave
 * unsold slots out: inc/view.php, p26_ad_shows()).
 *
 *   [data-ad]          the element to hide: the slot, or the section that
 *                      exists only for it (billboard, leaderboard…)
 *   [data-ad-label=g]  a "REKLAMA" label outside its slot; it goes when
 *                      every [data-ad="g"] beside it has gone
 *   [data-ad-host]     the section whose layout depends on the slot; it
 *                      takes .has-no-ad (its partial's layout without the
 *                      ad) when none of its slots is left
 *
 * A slot with a visible creative is never touched, so with ads the page is
 * exactly as before. A slot whose ad is still on its way (an image loading,
 * an ad network's script) keeps its reserved box; it is judged again when
 * the image loads or fails, when its content changes, and finally a few
 * seconds after the page has loaded. A slot the layout itself hides (the
 * desktop sections on a phone, the closed menu) is not judged until it
 * shows: ad blockers hide the creative inside a slot, never the slot.
 *
 * After the final pass a sample of views reports whether an ad was
 * blocked (inc/ad-stats.php): an anonymous count, no cookies.
 *
 * Ads added later (the photo viewer's: src/js/lightbox.js) go through
 * mountAd(): their scripts run, and their slots are judged the same way,
 * with the grace period counted from when they were added.
 */

const MEDIA = 'img, picture, iframe, video, object, embed, canvas, svg, amp-ad';
const LABELS = '.hp-ad__label, .hp-ad__size, [data-ad-label], .hp-video__ad-label, .hp-m-reels__ad-label, .hp-m-promo__label';
const GRACE = 3000; // ms after window load for script-filled slots
const MAX_REOPEN = 2; // a creative that keeps coming back and being hidden must not flicker forever

const EMPTY = 'is-ad-empty';
const NO_AD = 'has-no-ad';

const shown = (el) => el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
const sized = (el) => {
  const r = el.getBoundingClientRect();
  return r.width > 1 && r.height > 1;
};
// A failed image: its error event (caught below), or loaded with no pixels.
// An SVG without its own size also has naturalWidth 0, so it is not judged by that.
const errored = new WeakSet();
const failed = (img) => errored.has(img) || (img.complete && img.naturalWidth === 0 && !/\.svg(?:[?#]|$)/i.test(img.currentSrc || img.src));

/** Words in the slot that are not a "REKLAMA" label: a text ad. */
function hasText(slot) {
  const walker = document.createTreeWalker(slot, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    const el = node.parentElement;
    if (!node.textContent.trim() || !el || el.closest(LABELS) || el.closest('script, style, noscript, template') || !shown(el)) continue;
    return true;
  }
  return false;
}

/** filled | pending | empty | unknown (the layout does not display the slot). */
function judge(slot, final) {
  if (!shown(slot)) return 'unknown';

  let loading = false;
  for (const el of slot.querySelectorAll(MEDIA)) {
    if (el.closest(LABELS) || !shown(el)) continue;
    if (el.tagName === 'IMG') {
      if (!el.complete) { loading = true; continue; }
      if (failed(el)) continue;
    }
    if (sized(el)) return 'filled';
    if (el.tagName === 'IFRAME') loading = true; // a frame that has not sized itself yet
  }
  if (hasText(slot)) return 'filled';

  // An image still loading is never given up on; a script-filled slot is, after the grace period.
  if (loading) return 'pending';
  if (!final && slot.querySelector('script, ins:not([data-ad-status="unfilled"])')) return 'pending';
  return 'empty';
}

let track = null; // judges a slot added after load, or again (initAds)

/**
 * Ad markup added after load: el, already in the page, gets working scripts
 * (markup set as HTML does not run them; order kept, as when parsed) and its
 * [data-ad] slots are judged like the page's.
 */
export function mountAd(el) {
  for (const old of el.querySelectorAll('script')) {
    const script = document.createElement('script');
    for (const { name, value } of old.attributes) script.setAttribute(name, value);
    if (!old.hasAttribute('async')) script.async = false;
    script.text = old.text;
    old.replaceWith(script);
  }
  watchAds(el);
}

/** Judge el's slots (again): after mountAd(), and when a hidden container shows them. */
export function watchAds(el) {
  [...(el.matches('[data-ad]') ? [el] : []), ...el.querySelectorAll('[data-ad]')].forEach((slot) => track?.(slot));
}

export function initAds() {
  const slots = [...document.querySelectorAll('[data-ad]')];
  const initial = [...slots]; // the views reported on (report())

  // final: the slot's grace period is over — a script that filled nothing has failed.
  const state = new Map(slots.map((slot) => [slot, { verdict: '', reopened: 0, final: false }]));

  const hostOf = (slot) => slot.closest('[data-ad-host]');

  const refreshHost = (host) => {
    if (!host) return;
    const own = [...host.querySelectorAll('[data-ad]')].filter((s) => hostOf(s) === host);
    host.classList.toggle(NO_AD, own.length > 0 && own.every((s) => s.classList.contains(EMPTY)));
  };

  const refreshLabels = (slot) => {
    const group = slot.dataset.ad;
    const scope = slot.parentElement;
    if (!group || !scope) return;
    for (const label of scope.querySelectorAll(`:scope > [data-ad-label="${CSS.escape(group)}"]`)) {
      const mine = [...scope.querySelectorAll(`:scope > [data-ad="${CSS.escape(group)}"]`)];
      label.classList.toggle(EMPTY, mine.length > 0 && mine.every((s) => s.classList.contains(EMPTY)));
    }
  };

  const setEmpty = (slot, empty) => {
    if (slot.classList.contains(EMPTY) === empty) return;
    slot.classList.toggle(EMPTY, empty);
    refreshLabels(slot);
    refreshHost(hostOf(slot));
  };

  const check = (slot) => {
    if (slot.classList.contains(EMPTY)) return; // reopened only by new content (observer below)
    const verdict = judge(slot, state.get(slot).final);
    state.get(slot).verdict = verdict;
    if (verdict === 'empty') setEmpty(slot, true);
  };

  const checkAll = () => slots.forEach(check);

  // Changes inside a slot: judge it again; a creative added to a collapsed slot reopens it.
  const observer = 'MutationObserver' in window ? new MutationObserver((records) => {
    const touched = new Map();
    for (const r of records) {
      const target = r.target.nodeType === Node.ELEMENT_NODE ? r.target : r.target.parentElement;
      const slot = target?.closest('[data-ad]');
      if (!slot || (r.type === 'attributes' && target === slot)) continue; // our own class changes
      const added = r.type === 'childList' && [...r.addedNodes].some((n) => n.nodeType === Node.ELEMENT_NODE && (n.matches(MEDIA) || n.querySelector(MEDIA)));
      touched.set(slot, touched.get(slot) || added);
    }
    for (const [slot, added] of touched) {
      const s = state.get(slot);
      if (added && slot.classList.contains(EMPTY) && s.reopened < MAX_REOPEN) {
        s.reopened++;
        setEmpty(slot, false);
      }
      check(slot);
    }
  }) : null;
  const observe = (slot) => observer?.observe(slot, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'data-ad-status'] });
  slots.forEach(observe);

  track = (slot) => {
    if (state.has(slot)) { check(slot); return; }
    slots.push(slot);
    state.set(slot, { verdict: '', reopened: 0, final: false });
    observe(slot);
    check(slot);
    setTimeout(() => { state.get(slot).final = true; check(slot); }, GRACE);
  };

  // An image or frame in a slot loaded or failed (these events do not bubble: capture them).
  const onMedia = (event) => {
    if (event.type === 'error' && event.target instanceof HTMLImageElement) errored.add(event.target);
    const slot = event.target instanceof Element ? event.target.closest('[data-ad]') : null;
    if (slot) check(slot);
  };
  document.addEventListener('load', onMedia, true);
  document.addEventListener('error', onMedia, true);

  // Slots the layout did not display: judge them when it changes (rotation, resize).
  let resizing = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizing);
    resizing = setTimeout(() => slots.filter((s) => state.get(s).verdict === 'unknown').forEach(check), 200);
  }, { passive: true });

  // A sample of views tells the site how often ads are blocked (inc/ad-stats.php).
  const report = () => {
    const cfg = window.PORTALE2026 || {};
    const sample = Number(cfg.adStats?.sample) || 0;
    if (!cfg.ajaxUrl || !navigator.sendBeacon || Math.random() >= sample) return;
    const judged = initial.filter((s) => ['filled', 'empty'].includes(state.get(s).verdict) || s.classList.contains(EMPTY));
    if (!judged.length) return;
    const blocked = judged.some((s) => s.classList.contains(EMPTY)) ? '1' : '0';
    navigator.sendBeacon(cfg.ajaxUrl, new URLSearchParams({ action: 'portale2026_ad_view', blocked }));
  };

  const finish = () => {
    initial.forEach((slot) => { state.get(slot).final = true; });
    checkAll();
    report();
  };

  if (!slots.length) return; // nothing on the page; ads added later are still judged (track)
  checkAll();
  if (document.readyState === 'complete') setTimeout(finish, GRACE);
  else window.addEventListener('load', () => setTimeout(finish, GRACE), { once: true });
}
